import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  getPreferredUsername,
  storeLatestLocation,
} from './authenticationHelpers';

const basicConfiguration: Configuration = {
  auth: {
    authority: 'https://login.microsoftonline.com/common',
    clientId: '2d546d47-77af-46ff-8762-66073b81771f',
    navigateToLoginRequestUrl: false,
    redirectUri: `${window.location.protocol}//${window.location.host}/loginRedirect`,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const appScopes = {
  graph: 'https://graph.microsoft.com/.default',
  mnspro: `${basicConfiguration.auth.clientId}/.default`,
};

export const publicClientApplication = new PublicClientApplication(
  basicConfiguration,
);

export const initializeMsal = async (): Promise<void> => {
  await publicClientApplication.initialize();
};

export const acquireToken = async (
  instance: PublicClientApplication,
  account: AccountInfo,
  scopes: string[],
): Promise<string> => {
  const response = await instance
    .acquireTokenSilent({
      scopes: scopes,
      account: account,
    })
    .catch(error => {
      if (error instanceof InteractionRequiredAuthError) {
        if (window.location.pathname === '/login') {
          return;
        }

        // Needs to be set to get back to the origin before loggin in:
        storeLatestLocation();

        // Hard redirect to login in case of silent login issues:
        window.location.replace(
          `${window.location.protocol}//${window.location.host}/login`,
        );

        return {
          accessToken: '',
        } as AuthenticationResult;
      }
    });
  return response?.accessToken ?? '';
};

export const acquireGraphToken = (account: AccountInfo): Promise<string> =>
  acquireToken(publicClientApplication, account, [appScopes.graph]);

export const acquireMNSproCloudToken = (
  account: AccountInfo,
): Promise<string> =>
  acquireToken(publicClientApplication, account, [appScopes.mnspro]);

export const acquireActiveGraphToken = (): Promise<string | null> => {
  if (!publicClientApplication) {
    return Promise.resolve(null);
  }

  const preferred = getPreferredUsername();
  const allAccounts = publicClientApplication.getAllAccounts();
  if (allAccounts.length === 0) {
    return Promise.resolve(null);
  }

  const account = preferred
    ? allAccounts.find(a => a.username === preferred) ?? allAccounts[0]
    : allAccounts[0];

  if (!account) {
    return Promise.resolve(null);
  }

  return acquireGraphToken(account);
};

export const acquireActiveMNSproCloudToken = () => {
  if (!publicClientApplication) {
    return Promise.resolve(null);
  }

  const preferred = getPreferredUsername();
  const allAccounts = publicClientApplication.getAllAccounts();
  if (allAccounts.length === 0) {
    return Promise.resolve(null);
  }

  const account = preferred
    ? allAccounts.find(a => a.username === preferred) ?? allAccounts[0]
    : allAccounts[0];

  if (!account) {
    return Promise.resolve(null);
  }

  return acquireMNSproCloudToken(account);
};
